import { APP_ROUTE } from '@/app/app.route.constant';
import { API_URL } from '@/configs/api.config';
import { authLocalStorage } from '@/features/auths/utils/auth-local-storage';
import type { ApiResponse } from '@/types/api.type';
import Axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosRequestHeaders,
} from 'axios';

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

type RefreshResponseData = {
  refresh_token?: string | null;
  access_token?: string | null;
};

let refreshInFlight: Promise<string | null> | null = null;

function authRequestInterceptor(config: AuthRequestConfig) {
  const accessToken = authLocalStorage.getAccessToken();
  if (accessToken) {
    config.headers!.authorization = `Bearer ${accessToken}`;
  }
  return config;
}

function redirectToSignIn() {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const signInHref = APP_ROUTE.AUTH.SIGN_IN.getHref();
  if (window.location.pathname === signInHref) {
    return;
  }

  // Use full navigation from interceptor context to ensure app state resets cleanly.
  window.location.replace(APP_ROUTE.AUTH.SIGN_IN.getHref(currentPath));
}

async function rotateRefreshToken(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async function refreshSession() {
    const currentRefreshToken = authLocalStorage.getRefreshToken();
    const currentAccessToken = authLocalStorage.getAccessToken();
    if (!currentAccessToken || !currentAccessToken) {
      authLocalStorage.clearAuthTokens();
      return null;
    }

    try {
      const response = await Axios.post<ApiResponse<RefreshResponseData>>(
        `${API_URL}/auth/refresh`,
        { refresh_token: currentRefreshToken },
        {
          headers: {
            authorization: `Bearer ${currentAccessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const payload = response.data;

      const nextRefreshToken = payload.data?.refresh_token;
      const nextAccessToken = payload.data?.access_token;

      if (nextRefreshToken) {
        authLocalStorage.setRefreshToken(nextRefreshToken);
      }

      if (nextAccessToken) {
        authLocalStorage.setAccessToken(nextAccessToken);
        return nextAccessToken;
      }

      const existingAccessToken = authLocalStorage.getAccessToken();
      return existingAccessToken || null;
    } catch {
      authLocalStorage.clearAuthTokens();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export const apiClient = Axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(authRequestInterceptor);
apiClient.interceptors.response.use(
  response => {
    return response.data;
  },
  async (error: AxiosError) => {
    const message =
      (error.response?.data as ApiResponse<unknown>)?.message || error.message;
    console.error('API Error AXIOS:', message);

    const requestConfig = error.config as AuthRequestConfig | undefined;
    const status = error.response?.status || error.status;
    const requestUrl = requestConfig?.url || '';

    if (
      status === 401 &&
      requestConfig &&
      !requestConfig._retry &&
      !requestUrl.includes('/auth/refresh')
    ) {
      requestConfig._retry = true;

      const nextAccessToken = await rotateRefreshToken();
      if (nextAccessToken) {
        requestConfig.headers = (requestConfig.headers ||
          {}) as AxiosRequestHeaders;
        requestConfig.headers.authorization = `Bearer ${nextAccessToken}`;
        return apiClient.request(requestConfig);
      }
    }

    if (status === 401) {
      redirectToSignIn();
    }

    return Promise.reject(error);
  }
);
